import { Vector3 } from "three";
import { useRef, useState } from "react";
import { useModels } from "../Resources/ModelProvider.tsx";
import { useControls } from "leva";
import EditorPrototype from "./EditorPrototype.tsx";
import { useEditorStore } from "./stores/editorStore.ts";
import { ThreeEvent } from "@react-three/fiber";
import { useSpring, animated } from "@react-spring/three";

type EditorTileProps = {
  tilePosition: Vector3;
  gridPosition: Vector3;
  tileSize: number;
  prototype: EditorPrototype | null;
  editable: boolean;
  onEditableTileLeftClick: (position: Vector3) => void;
  onEditableTileRightClick: (position: Vector3) => void;
};

export default function EditorTile(props: EditorTileProps): JSX.Element {
  const selectedPrototype = useEditorStore((state) => state.selectedPrototype); // TODO : load only if uneditable or protptype != null

  const setModelOpacity = (model: any, opacity: number) => {
    model.traverse((child: any) => {
      if (child.isMesh) {
        const newMaterial = child.material.clone();
        newMaterial.opacity = opacity;
        newMaterial.transparent = true;
        child.material = newMaterial;
      }
    });
  };

  let model = null;
  const models = useModels().models;
  if (props.prototype) {
    model = models.get(props.prototype.mesh_name)!.clone();
  } else if (props.editable && selectedPrototype) {
    model = models.get(selectedPrototype.mesh_name)!.clone();
    setModelOpacity(model, 0.25);
  }

  const [spawned, setSpawned] = useState(props.prototype !== null);

  const [{ scaleY }] = useSpring(
    {
      scaleY: spawned ? [1] : [0.9],
      config: {
        mass: 2,
        friction: 35,
        tension: 600,
      },
    },
    [spawned],
  );

  const [{ scaleXZ }] = useSpring(
    {
      scaleXZ: spawned ? [1] : [0.9],
      config: {
        mass: 2,
        friction: 40,
        tension: 900,
      },
    },
    [spawned],
  );

  const spawn = () => {
    console.log("Spawning");
    setSpawned(true);
  };

  const despawn = () => {
    console.log("Despawning");
    setSpawned(false);
  };

  // Debug
  const { enableAxesHelper } = useControls("Tile", {
    enableAxesHelper: false,
  });

  // Position of the tile (assigned to the "group")
  const tilePosition = props.tilePosition as Vector3;
  const tileGroupPosition = new Vector3(
    tilePosition.x,
    tilePosition.y - props.tileSize / 2,
    tilePosition.z,
  );
  const meshPositionWithinTileGroup = new Vector3(0, props.tileSize / 2, 0);

  const [rotation, setRotation] = useState(0);

  const editableTilePrototype = useRef(null);

  const changeVisibility = (visible: boolean) => {
    if (editableTilePrototype.current) {
      // @ts-ignore TODO : fix this
      editableTilePrototype.current.visible = visible;
    }
  };

  const handleLeftClick = (event: ThreeEvent<MouseEvent>): void => {
    spawn();
    props.onEditableTileLeftClick(props.gridPosition);
  };

  const handleRightClick = (event: ThreeEvent<MouseEvent>): void => {
    despawn();
    props.onEditableTileRightClick(props.gridPosition);
  };

  return props.prototype && model ? (
    <animated.group
      scale-x={scaleXZ}
      scale-y={scaleY}
      scale-z={scaleXZ}
      position={tileGroupPosition}
      rotation={[
        0,
        (Math.PI / 2) * props.prototype.mesh_rotation + rotation,
        0,
      ]}
      onContextMenu={(event) => {
        event.stopPropagation();
        handleRightClick(event);
      }}
    >
      {enableAxesHelper && <axesHelper args={[1]} />}
      <primitive
        position={meshPositionWithinTileGroup}
        object={model}
        key={props.prototype.mesh_name + props.gridPosition}
        scale={[2, 2, 2]}
      />
    </animated.group>
  ) : props.editable ? (
    <group>
      {/*Placement Tile, invisible*/}
      <mesh
        position={tilePosition}
        rotation={[-Math.PI / 2, 0, 0]}
        onClick={(event) => {
          event.stopPropagation();
          handleLeftClick(event);
        }}
        onPointerOver={(event) => {
          event.stopPropagation();
          changeVisibility(true);
        }}
        onPointerOut={(event) => {
          event.stopPropagation();
          changeVisibility(false);
        }}
      >
        <planeGeometry args={[props.tileSize, props.tileSize]} />
        <meshStandardMaterial visible={false} />
      </mesh>
      {/*Tile Preview*/}
      {model && selectedPrototype ? (
        <group
          ref={editableTilePrototype}
          visible={false}
          position={tilePosition}
          rotation={[
            0,
            (Math.PI / 2) * selectedPrototype.mesh_rotation + rotation,
            0,
          ]}
        >
          {enableAxesHelper && <axesHelper args={[1]} />}
          <primitive
            object={model}
            key={selectedPrototype.mesh_name + props.gridPosition}
            scale={[2, 2, 2]}
          />
        </group>
      ) : (
        <></>
      )}
    </group>
  ) : (
    <></>
  );
}
